/*
 *
 * MainLayout reducer
 *
 */

import transformCart from './transformCart';
import { ContainerActions, ContainerState, IBreadCrumbMarkup } from './types';

import { flatMap } from 'lodash';
import qs from 'qs';
import { combineReducers } from 'redux';
import { NotificationMessage, ReadStatus } from 'types/schema';
import { LANGUAGES } from 'utils/constants';
import { pathnameChecker } from 'utils/url';
import ActionTypes from './constants';

export const initialState: ContainerState = {
  store: { loading: true, location: {} },
  sideNav: false,
  cart: {
    loading: true,
    cartSet: [],
    store: undefined,
    notAvailableProducts: [],
    deliveryFee: {
      isFreeDelivery: false,
      fee: 0,
      remainingToFree: 0,
      deliveryFeeThreshold: 0,
      additionalVat: 0,
      areaGroupName: '',
    },
  },
  addedProducts: {},
  user: { loading: true },
  buyer: { stores: [], loading: true, redInvoiceInfo: {}, enabledFeatures: [], name: '', id: '', userGroups: [] },
  categories: [],
  categoriesObject: {},
  brandsByCategory: [],
  favoriteCategories: [],
  addingProducts: {},
  suppliers: { supplierNoFilter: [], supplierHasOrders: [] },
  lang: window.localStorage.getItem('lang') || 'vi',
  orderCountSummary: { countPendingOrders: 0 },
  updateQuantityProducts: [],
  orderDisabled: { orderDisabled: false },
  notification: { id: '', submitting: false, loading: false },
  notificationMessages: { loading: false, data: [], totalPage: 0, totalResults: 0 },
  hasUnreadNotification: false,
  breadCrumbDataMarkup: '',
  promotionDataPopup: null,
  searchProductResults: { totalPage: 0, totalResults: 0, data: [], loading: false },
  buyerWebVersion: '',
  isOpenAlcoholPopup: false,
  isUnder18: true,
  viewportWidth: window.document.body.clientWidth,
  vaInfo: {},
  isConfirmLocation: false,
  recommendationProductsForNewCustomer: [],
  isOpenRecommendationProductsPopup: false,
  specialDates: new Map(),
  productTags: [],
  searchHistory: [],
  trendingSearchData: {},
  recommendSkusForLoggedInUser: [],
  recommendNewWardForStore: [],
};

function storeReducer(state = initialState.store, action: ContainerActions) {
  const { type, payload } = action as any;

  switch (type) {
    case ActionTypes.SET_SELECTED_STORE:
      return { ...payload, loading: false };
    default:
      return state;
  }
}

function sideNavReducer(state = initialState.sideNav, action: ContainerActions) {
  const { type } = action as any;
  switch (type) {
    case ActionTypes.SIDE_NAV_OPEN:
      localStorage.setItem('sideNavOpened', 'true');
      if (window.document.body.clientWidth < 1025) {
        window.document.body.classList.add('hideScroll');
      }
      return true;
    case ActionTypes.SIDE_NAV_CLOSE:
      localStorage.setItem('sideNavOpened', 'false');
      if (window.document.body.clientWidth < 1025) {
        window.document.body.classList.remove('hideScroll');
      }
      return false;
    case ActionTypes.SIDE_NAV_TOGGLE:
      const sideNav = !state;
      localStorage.setItem('sideNavOpened', sideNav.toString());
      if (window.document.body.clientWidth < 1025) {
        sideNav
          ? window.document.body.classList.add('hideScroll')
          : window.document.body.classList.remove('hideScroll');
      }
      return sideNav;
    default:
      return state;
  }
}

function cartReducer(state = initialState.cart, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.FETCH_CART_SUCCESS: {
      return {
        ...state,
        loading: false,
        ...payload,
        // cartSet: sortCartSetByProductName(payload.cartSet),
      };
    }
    case ActionTypes.FETCH_CART_REQUEST:
      return { ...state, loading: true };

    case ActionTypes.FETCH_CART_FAILURE:
      return { ...state, loading: false };

    case ActionTypes.UPDATE_CART_SUCCESS:
    case ActionTypes.REMOVE_ITEM_TO_CART_SUCCESS:
    case ActionTypes.ADD_TO_CART_SUCCESS:
      const cart = payload.carts ? transformCart(state, payload) : payload;
      return {
        ...state,
        loading: false,
        ...cart,
        // cartSet: sortCartSetByProductName(cart.cartSet),
      };
    case ActionTypes.UPDATE_NOTE_TO_CART:
      return {
        ...state,
        cartSet: state.cartSet.map((cart) => ({
          ...cart,
          items: cart.items.map((item) =>
            item.product.id === payload.id ? { ...item, product: { ...item.product, note: payload.note } } : item,
          ),
        })),
      };
    case ActionTypes.UPDATE_NOT_AVAILABLE_PRODUCTS:
      return {
        ...state,
        notAvailableProducts: payload,
      };

    case ActionTypes.UPDATE_DELIVERY_NOTE:
      return {
        ...state,
        cartSet: payload.cartSet.map((cart) => ({
          ...state.cartSet.find((item) => item.supplier.id === cart.supplier.id),
          deliveryNotes: cart.deliveryNotes,
          deliveryDatetime: cart.deliveryDatetime,
          deliveryTimeSlots: cart.deliveryTimeSlots,
          earliestDate: cart.earliestDate,
          orderDeadline: cart.orderDeadline,
        })),
      };
    case ActionTypes.GET_DELIVERY_FEE_SUCCESS:
      return {
        ...state,
        deliveryFee: {
          ...state.deliveryFee,
          ...payload,
        },
      };
    case ActionTypes.GET_DELIVERY_FEE_FAILURE:
      return {
        ...state,
        deliveryFee: {
          isFreeDelivery: false,
          fee: 0,
          remainingToFree: 0,
          deliveryFeeThreshold: 0,
          additionalVat: 0,
          areaGroupName: '',
        },
      };

    default:
      return state;
  }
}

function addingProductsReducer(state = initialState.addingProducts, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.ADDING_TO_CART:
      return { ...state, [payload.productId]: true };
    case ActionTypes.ADDING_MULTIPLE_TO_CART:
      return { ...state, ...payload };
    case ActionTypes.ADDED_MULTIPLE_TO_CART:
      return { ...state, ...payload };
    case ActionTypes.ADDED_TO_CART:
      return { ...state, [payload.productId]: false };
    default:
      return state;
  }
}

function addedProductsReducer(state = initialState.addedProducts, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.FETCH_CART_SUCCESS: {
      const addedProducts = {};
      if (payload.cartSet[0]) {
        payload.cartSet[0].items.forEach((item) => {
          addedProducts[item.product.id] = Number(item.quantity.toFixed(3));
        });
      }
      return addedProducts;
    }
    case ActionTypes.UPDATE_CART_SUCCESS:
    case ActionTypes.REMOVE_ITEM_TO_CART_SUCCESS:
    case ActionTypes.ADD_TO_CART_SUCCESS:
      if (payload.carts) {
        return flatMap(payload.carts, ({ items }) => items).reduce((acc, { item: { id }, orderedQty }) => {
          acc[id] = Number(orderedQty);
          return acc;
        }, {});
      }
      return flatMap(payload.cartSet, ({ items }) => items).reduce((acc, { product: { id }, quantity }) => {
        acc[id] = quantity;
        return acc;
      }, {});
    default:
      return state;
  }
}

function userReducer(state = initialState.user, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.FETCH_USER_SUCCESS:
      return { ...state, ...payload, loading: false };
    default:
      return state;
  }
}

function buyerReducer(state = initialState.buyer, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.FETCH_BUYER_SUCCESS:
      return { ...payload, stores: payload.stores || [], loading: false };
    case ActionTypes.UPDATE_ENABLED_FEATURES:
      const enabledFeatures = payload.enabled
        ? [...state?.enabledFeatures, payload.feature]
        : state.enabledFeatures.filter((feature) => feature !== payload.feature);
      return {
        ...state,
        enabledFeatures: enabledFeatures,
      };
    default:
      return state;
  }
}

function categoriesReducer(state = initialState.categories, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.FETCH_CATEGORIES_SUCCESS:
      return [
        // This should be translate & return from server side
        ...payload,
      ];
    default:
      return state;
  }
}

function categoriesObjectReducer(state = initialState.categoriesObject, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.FETCH_CATEGORIES_SUCCESS:
      const generateCategoriesObject = (data, isParentCategory = false) =>
        data.reduce((acc, category) => {
          acc[category?.id] = {
            ...category,
            isParentCategory: isParentCategory,
          };
          if (category?.children?.length) {
            return { ...acc, ...generateCategoriesObject(category?.children) };
          }
          return acc;
        }, {});
      const categoriesObject = generateCategoriesObject(payload, true);
      return categoriesObject;
    default:
      return state;
  }
}

function brandsByCategoryReducer(state = initialState.brandsByCategory, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.FETCH_BRANDS_BY_CATEGORY_SUCCESS:
      const category = state?.find((item) => item.categoryId === payload.categoryId);
      if (category) {
        return state.map((item) => (item.categoryId === payload.categoryId ? { ...payload } : item));
      }
      return [...state, payload];
    default:
      return state;
  }
}

function favoriteCategoriesReducer(state = initialState.favoriteCategories, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.FETCH_FAVORITE_CATEGORIES_SUCCESS:
      return [...payload];
    default:
      return state;
  }
}

function langReducer(state = initialState.lang, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.SET_LANG:
      window.localStorage.setItem('lang', payload);
      if (
        pathnameChecker.isProductPage(window.location.pathname) ||
        pathnameChecker.isCategoryPage(window.location.pathname) ||
        pathnameChecker.isLandingPage(window.location.pathname)
      ) {
        if (payload === LANGUAGES.Vi) {
          // const langRegex = new RegExp(`[?]*lang=(${Object.values(LANGUAGES).join('|')})&\?`, 'g');
          const search = qs.parse(window.location.search, { ignoreQueryPrefix: true });
          if (search.lang) {
            delete search.lang;
          }
          const query = qs.stringify({ ...search });
          const newUrl = window.location.href.replace(/\?.*$/, '').concat(query ? `?${query}` : '');
          window.location.replace(newUrl);
          // window.location.href = window.location.href.replace(langRegex, '');
        } else {
          const search = qs.parse(window.location.search, { ignoreQueryPrefix: true });
          const query = qs.stringify({ lang: payload, ...search });
          const newUrl = window.location.href.replace(/\?[\w\d=]+/gi, '').concat(query ? `?${query}` : '');
          window.location.replace(newUrl);
        }
      } else {
        window.location.reload();
      }
      return payload;
    default:
      return state;
  }
}

function shortSuppliersReducer(state = initialState.suppliers, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.FETCH_ALL_SUPPLIER_SUCCESS:
    case ActionTypes.FETCH_SHORT_SUPPLIER_SUCCESS:
      return { ...state, ...payload };
    default:
      return state;
  }
}

function orderCountSummary(state = initialState.orderCountSummary, action: ContainerActions) {
  const { type, payload } = action as any;

  switch (type) {
    case ActionTypes.FETCH_RECENT_ORDERS_SUCCESS:
      return { ...state, ...payload };
    default:
      return state;
  }
}

function updateQuantityProductsReducer(state = initialState.updateQuantityProducts, action: ContainerActions) {
  const { type, payload } = action as any;

  switch (type) {
    case ActionTypes.UPDATE_MULTIPLE_PRODUCT_QUANTITY:
      return [...state, payload];
    case ActionTypes.ADDED_MULTIPLE_TO_CART:
      return [];
    default:
      return state;
  }
}

function orderDisabled(state = initialState.orderDisabled, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.FETCH_ORDER_DISABLED_SUCCESS:
      return { ...state, ...payload };
    default:
      return state;
  }
}

function notificationReducer(state = initialState.notification, action: ContainerActions) {
  const { type, payload } = action as any;

  switch (type) {
    case ActionTypes.NOTIFICATION_REGISTER_TOKEN_REQUEST:
      return { ...state, id: '', submitting: true };
    case ActionTypes.NOTIFICATION_REGISTER_TOKEN_SUCCESS:
      return { ...state, id: payload.id, submitting: false };
    case ActionTypes.NOTIFICATION_REGISTER_TOKEN_FAILURE:
      return { ...state, submitting: false };

    case ActionTypes.NOTIFICATION_UNREGISTER_TOKEN:
      return { ...state, id: '' };
    default:
      return state;
  }
}

const notificationMessagesReducer = (state = initialState.notificationMessages, action: ContainerActions) => {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.NOTIFICATIONS_REQUEST:
      return { ...state, loading: true };
    case ActionTypes.NOTIFICATIONS_SUCCESS:
      return { ...state, ...payload, loading: false };
    case ActionTypes.NOTIFICATIONS_FAILURE:
      return { ...state, loading: false };

    case ActionTypes.MARK_NOTIFICATION_AS_READ_SUCCESS: {
      return {
        ...state,
        data: state?.data?.map((item: NotificationMessage) => ({
          ...item,
          readStatus: item.id === payload.messageId ? ReadStatus.IsRead : item.readStatus,
        })),
      };
    }

    default: {
      return state;
    }
  }
};

const hasUnreadNotificationReducer = (state = initialState.hasUnreadNotification, action: ContainerActions) => {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.SET_HAS_UNREAD_NOTIFICATION:
      return payload;
    default: {
      return state;
    }
  }
};

function breadCrumbDataMarkupReducer(state = initialState.breadCrumbDataMarkup, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.SET_BREAD_CRUMB_DATA_MARKUP: {
      const result = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        // '@id': `${window.location.href}/#breadcrumb`,
      };
      if (payload.length) {
        const itemListElement: IBreadCrumbMarkup[] = payload.map((item, index) => {
          const itemBreadCrumb: IBreadCrumbMarkup = {
            '@type': 'ListItem',
            position: index + 1,
            name: item.nameLocal,
          };
          if (item.path) {
            itemBreadCrumb.item =
              process.env.APP_ENV === 'production'
                ? `https://kamereo.vn${item?.path}`
                : `https://buyer.qa.kamereo.vn${item?.path}`;
          }
          return itemBreadCrumb;
        });
        return JSON.stringify({
          ...result,
          itemListElement: itemListElement,
        });
      }
      return JSON.stringify(result);
    }
    default:
      return state;
  }
}

function promotionDataPopupReducer(state = initialState.promotionDataPopup, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.SET_PROMOTION_DATA_POPUP: {
      return payload;
    }
    default:
      return state;
  }
}

function searchProductResultsReducer(state = initialState.searchProductResults, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.SET_SEARCH_PRODUCT_RESULTS_LOADING: {
      return {
        ...state,
        loading: payload,
      };
    }
    case ActionTypes.SET_SEARCH_PRODUCT_RESULTS_SUCCESS: {
      return {
        ...state,
        totalPage: payload.totalPage || 0,
        totalResults: payload.totalResults || 0,
        data: payload.data?.length ? [...state.data, ...payload.data] : [],
      };
    }
    default:
      return state;
  }
}

function buyerWebVersionReducer(state = initialState.buyerWebVersion, action: ContainerActions) {
  const { payload, type } = action as any;
  switch (type) {
    case ActionTypes.SET_BUYER_WEB_VERSION: {
      return payload.version;
    }
    default:
      return state;
  }
}

function alcoholPopupReducer(state = initialState.isOpenAlcoholPopup, action: ContainerActions) {
  const { payload, type } = action as any;
  switch (type) {
    case ActionTypes.SET_ALCOHOL_STATE: {
      return payload;
    }
    default:
      return state;
  }
}

function isUnder18Reducer(state = initialState.isUnder18, action: ContainerActions) {
  const { payload, type } = action as any;
  switch (type) {
    case ActionTypes.SET_ALCOHOL_AGE_STATE: {
      return payload;
    }
    default:
      return state;
  }
}

function viewportReducer(state = initialState.viewportWidth, action: ContainerActions) {
  const { payload, type } = action as any;
  switch (type) {
    case ActionTypes.SET_VIEWPORT_WIDTH: {
      return payload;
    }
    default:
      return state;
  }
}

function vaInfoReducer(state = initialState.vaInfo, action: ContainerActions) {
  const { payload, type } = action as any;
  switch (type) {
    case ActionTypes.FETCH_VA_INFO_SUCCESS: {
      return payload;
    }
    default:
      return state;
  }
}

function confirmLocationReducer(state = initialState.isConfirmLocation, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.SET_CONFIRM_LOCATION_ADDRESS: {
      return payload;
    }
    default:
      return state;
  }
}

function recommendationProductsForNewCustomerReducer(
  state = initialState.recommendationProductsForNewCustomer,
  action: ContainerActions,
) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.SET_RECOMMENDATION_PRODUCTS_FOR_NEW_CUSTOMER: {
      return payload;
    }
    default:
      return state;
  }
}

function isOpenRecommendationProductsPopupReducer(
  state = initialState.isOpenRecommendationProductsPopup,
  action: ContainerActions,
) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.SET_IS_SHOW_RECOMMENDATION_PRODUCTS_POPUP: {
      return payload;
    }
    default:
      return state;
  }
}

function specialDatesReducer(state = initialState.specialDates, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.SET_SPECIAL_DATES: {
      return payload;
    }
    default:
      return state;
  }
}

function productTagsReducer(state = initialState.productTags, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.SET_PRODUCT_TAGS: {
      return payload;
    }
    default:
      return state;
  }
}

function searchHistoryReducer(state = initialState.searchHistory, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.SET_SEARCH_HISTORY: {
      return payload;
    }
    default:
      return state;
  }
}

function trendingSearchDataReducer(state = initialState.trendingSearchData, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.SET_TRENDING_SEARCH_FOR_GUEST_USER: {
      return payload;
    }
    default:
      return state;
  }
}

function recommendSkusForLoggedInUserReducer(
  state = initialState.recommendSkusForLoggedInUser,
  action: ContainerActions,
) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.SET_RECOMMEND_SKUS_FOR_SIGNIN_USER: {
      return payload;
    }
    default:
      return state;
  }
}

function recommendNewWardForStoreReducer(state = initialState.recommendNewWardForStore, action: ContainerActions) {
  const { type, payload } = action as any;
  switch (type) {
    case ActionTypes.SET_RECOMMEND_NEW_WARD_FOR_STORE: {
      return payload;
    }
    default:
      return state;
  }
}

export default combineReducers({
  store: storeReducer,
  sideNav: sideNavReducer,
  cart: cartReducer,
  addedProducts: addedProductsReducer,
  addingProducts: addingProductsReducer,
  updateQuantityProducts: updateQuantityProductsReducer,
  user: userReducer,
  buyer: buyerReducer,
  categories: categoriesReducer,
  brandsByCategory: brandsByCategoryReducer,
  favoriteCategories: favoriteCategoriesReducer,
  lang: langReducer,
  suppliers: shortSuppliersReducer,
  orderCountSummary: orderCountSummary,
  orderDisabled: orderDisabled,
  notification: notificationReducer,
  notificationMessages: notificationMessagesReducer,
  hasUnreadNotification: hasUnreadNotificationReducer,
  breadCrumbDataMarkup: breadCrumbDataMarkupReducer,
  promotionDataPopup: promotionDataPopupReducer,
  searchProductResults: searchProductResultsReducer,
  buyerWebVersion: buyerWebVersionReducer,
  isOpenAlcoholPopup: alcoholPopupReducer,
  isUnder18: isUnder18Reducer,
  viewportWidth: viewportReducer,
  vaInfo: vaInfoReducer,
  isConfirmLocation: confirmLocationReducer,
  recommendationProductsForNewCustomer: recommendationProductsForNewCustomerReducer,
  isOpenRecommendationProductsPopup: isOpenRecommendationProductsPopupReducer,
  specialDates: specialDatesReducer,
  productTags: productTagsReducer,
  searchHistory: searchHistoryReducer,
  categoriesObject: categoriesObjectReducer,
  trendingSearchData: trendingSearchDataReducer,
  recommendSkusForLoggedInUser: recommendSkusForLoggedInUserReducer,
  recommendNewWardForStore: recommendNewWardForStoreReducer,
});
